import React, { useCallback } from 'react';
import styled from 'styled-components';
import { layout, media } from '@prisjakt/ui-base';

import {
  H4Text,
  BodySmallText,
  TitleMediumText,
  FontColor,
} from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';
import CloseIcon from 'mdi-react/CloseIcon';
import HistoryIconComponent from 'mdi-react/HistoryIcon';
import Clickable from '@prisjakt/ui-clickable';

import { ListItem, ListItemButton } from './styles-new';

import { useAppContext } from '@prisjakt/ui-app-context';

import {
  trackSearchHistoryClicked,
  trackSearchHistoryDeleted,
} from '../../tracking';

const ORDER = 100;

type Props = {
  text: string;
  selected: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClear: () => void;
  showClear: boolean;
};

export const HistoryItem = (props: Props) => {
  const { text, onDelete, onClick, selected, onClear, showClear } = props;

  const { t } = useAppContext();

  const handleOnClick = useCallback(() => {
    onClick();
    trackSearchHistoryClicked(text);
  }, [onClick, text]);

  const handleOnDelete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onDelete(event);
      trackSearchHistoryDeleted(text);
    },
    [onDelete, text]
  );

  return (
    <>
      {showClear ? (
        <ClearButton color={FontColor.PRIMARY}>
          <Clickable onClick={onClear}>{t('common', 'button.clear')}</Clickable>
        </ClearButton>
      ) : null}

      <ListItem $selected={selected} order={ORDER}>
        <ListItemButton onClick={handleOnClick}>
          <ItemInner gap={0.5}>
            <HistoryIcon size={24} />

            <BodySmallText>{text}</BodySmallText>
          </ItemInner>
        </ListItemButton>

        <RemoveButton onClick={handleOnDelete}>
          <CloseIcon size={12} />
        </RemoveButton>
      </ListItem>
    </>
  );
};

const { spacingSmall, spacingNormal } = layout.measurements;

const ItemInner = styled(Row)`
  align-items: center;
  padding: ${spacingSmall} 0;
`;

const RemoveButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.bg.secondary};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 150ms ease;
  color: ${p => p.theme.icons.primary};
  position: absolute;
  top: 50%;
  right: ${layout.measurements.spacingNormal};
  transform: translateY(-50%);

  &:hover {
    background-color: ${p => p.theme.bg.tertiary};
  }
`;

const HistoryIcon = styled(HistoryIconComponent)`
  color: ${p => p.theme.icons.tertiary};
`;

export const Title = styled(H4Text)`
  order: ${ORDER};
  margin-left: ${spacingNormal};
  margin-top: ${spacingNormal};
  flex: 0 1 auto;
`;

const ClearButton = styled(TitleMediumText)`
  flex: 0 1 auto;
  order: ${ORDER + 1};
  margin-left: ${spacingNormal};
  margin-top: ${spacingNormal};

  ${media.mediumUp`
    display: none;
  `};

  button {
    color: inherit;
    cursor: pointer;
  }
`;
